// src/pages/atributos/NuevoTipoAtributo.js
import React, { useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import InputGroup from 'react-bootstrap/InputGroup'

function NuevoTipoAtributo({ mostrar, guardar, cancelar }) {
	const [nombre, setNombre] = useState('')
	const [descripcion, setDescripcion] = useState('')
	const [activo, setActivo] = useState(true)
	const [valores, setValores] = useState([])
	const [nuevoValor, setNuevoValor] = useState('')
	const [errores, setErrores] = useState({})
	const [loading, setLoading] = useState(false)

	const handleClose = () => {
		resetForm()
		cancelar()
	}

	const resetForm = () => {
		setNombre('')
		setDescripcion('')
		setActivo(true)
		setValores([])
		setNuevoValor('')
		setErrores({})
	}

	const validar = () => {
		const nuevosErrores = {}

		if (!nombre.trim()) {
			nuevosErrores.nombre = 'El nombre es obligatorio'
		}

		setErrores(nuevosErrores)
		return Object.keys(nuevosErrores).length === 0
	}

	const handleSubmit = async (e) => {
		e.preventDefault()

		if (!validar()) return

		setLoading(true)

		try {
			await guardar({
				nombre: nombre.trim(),
				descripcion: descripcion.trim() || null,
				activo,
				valores: valores.map((v, index) => ({
					nombre: v,
					orden: index + 1,
					activo: true
				}))
			})
			resetForm()
		} catch (error) {
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	const agregarValor = () => {
		const valorTrimmed = nuevoValor.trim()
		
		if (!valorTrimmed) return

		if (valores.includes(valorTrimmed)) {
			setErrores({ ...errores, nuevoValor: 'Este valor ya existe' })
			return
		}

		setValores([...valores, valorTrimmed])
		setNuevoValor('')
		setErrores({ ...errores, nuevoValor: null })
	}

	const eliminarValor = (index) => {
		setValores(valores.filter((_, i) => i !== index))
	}

	const handleKeyPress = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			agregarValor()
		}
	}

	return (
		<Modal show={mostrar} onHide={handleClose} size="lg" centered>
			<Modal.Header closeButton>
				<Modal.Title>
					<i className="fa fa-plus-circle text-primary me-2"></i>
					Nuevo tipo de atributo
				</Modal.Title>
			</Modal.Header>
			<Form onSubmit={handleSubmit}>
				<Modal.Body>
					<Form.Group className="mb-3" controlId="nombre">
						<Form.Label>Nombre *</Form.Label>
						<Form.Control
							type="text"
							value={nombre}
							onChange={(e) => setNombre(e.target.value)}
							placeholder="Ej: Talla Bebé, Color, Material..."
							isInvalid={!!errores.nombre}
							autoFocus
						/>
						<Form.Control.Feedback type="invalid">
							{errores.nombre}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group className="mb-3" controlId="descripcion">
						<Form.Label>Descripción</Form.Label>
						<Form.Control
							as="textarea"
							rows={2}
							value={descripcion}
							onChange={(e) => setDescripcion(e.target.value)}
							placeholder="Descripción opcional del tipo de atributo..."
						/>
					</Form.Group>

					<Form.Group className="mb-4" controlId="activo">
						<Form.Check
							type="switch"
							label="Activo"
							checked={activo}
							onChange={(e) => setActivo(e.target.checked)}
						/>
						<Form.Text className="text-muted">
							Solo los tipos activos estarán disponibles para asignar a categorías y productos
						</Form.Text>
					</Form.Group>

					<hr />

					<h6 className="mb-3">
						<i className="fa fa-list me-2"></i>
						Valores del atributo
					</h6>
					<p className="text-muted small mb-3">
						Agregue los valores posibles para este tipo de atributo. 
						Por ejemplo, para "Talla Bebé": RN, 0-3M, 3-6M, etc.
					</p>

					<Form.Group className="mb-3">
						<InputGroup>
							<Form.Control
								type="text"
								value={nuevoValor}
								onChange={(e) => setNuevoValor(e.target.value)}
								onKeyPress={handleKeyPress}
								placeholder="Escribir valor y presionar Enter o click en Agregar"
								isInvalid={!!errores.nuevoValor}
							/>
							<Button 
								variant="outline-primary" 
								onClick={agregarValor}
								disabled={!nuevoValor.trim()}
							>
								<i className="fa fa-plus me-1"></i>
								Agregar
							</Button>
						</InputGroup>
						{errores.nuevoValor && (
							<Form.Text className="text-danger">
								{errores.nuevoValor}
							</Form.Text>
						)}
					</Form.Group>

					{valores.length > 0 ? (
						<div className="valores-preview">
							<small className="text-muted d-block mb-2">
								{valores.length} valor(es) agregado(s):
							</small>
							<div className="d-flex flex-wrap gap-2">
								{valores.map((valor, index) => (
									<Badge 
										key={index} 
										bg="secondary" 
										className="valor-badge d-flex align-items-center"
									>
										{valor}
										<Button
											variant="link"
											size="sm"
											className="p-0 ms-2 text-white"
											onClick={() => eliminarValor(index)}
										>
											<i className="fa fa-times"></i>
										</Button>
									</Badge>
								))}
							</div>
						</div>
					) : (
						<div className="text-center py-3 bg-light rounded">
							<i className="fa fa-info-circle text-muted me-1"></i>
							<small className="text-muted">
								Puede agregar valores ahora o después de crear el tipo
							</small>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
						Cancelar
					</Button>
					<Button variant="primary" type="submit" disabled={loading}>
						{loading ? (
							<>
								<i className="fa fa-spinner fa-spin me-2"></i>
								Guardando...
							</>
						) : (
							<>
								<i className="fa fa-save me-2"></i>
								Crear tipo de atributo
							</>
						)}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	)
}

export default NuevoTipoAtributo